import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AuthApi } from '../../services/authApi';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const REDIRECT_URL = 'https://learning.m2mba.com';
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!success) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      window.location.href = REDIRECT_URL;
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [success]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');

    // Real-time password strength validation for new password
    if (name === 'newPassword') {
      const validation = AuthApi.validatePasswordStrength(value);
      setValidationErrors(validation.errors);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate fields
    if (!formData.currentPassword) {
      setError('Vui lòng nhập mật khẩu hiện tại');
      return;
    }

    if (!formData.newPassword) {
      setError('Vui lòng nhập mật khẩu mới');
      return;
    }

    if (!formData.confirmPassword) {
      setError('Vui lòng xác nhận mật khẩu mới');
      return;
    }

    // Validate password strength
    const validation = AuthApi.validatePasswordStrength(formData.newPassword);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      setValidationErrors(validation.errors);
      return;
    }

    // Check if passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }

    // Check if new password is different from current
    if (formData.currentPassword === formData.newPassword) {
      setError('Mật khẩu mới phải khác mật khẩu hiện tại');
      return;
    }

    setIsLoading(true);

    try {
      await AuthApi.changePassword(formData.currentPassword, formData.newPassword, formData.confirmPassword);
      setSuccess(true);
      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setValidationErrors([]);
    } catch (err: any) {
      setError(err.message || 'Đổi mật khẩu thất bại. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess(false);
    setValidationErrors([]);
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    onClose();
  };

  const getPasswordStrengthColor = () => {
    if (validationErrors.length === 0 && formData.newPassword.length > 0) {
      return 'text-m2-secondary';
    } else if (validationErrors.length <= 3 && formData.newPassword.length > 0) {
      return 'text-yellow-600';
    } else {
      return 'text-red-600';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-eh bg-eh-cream p-6 text-left align-middle shadow-eh-soft ring-1 ring-eh-border transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-m2-on-surface"
                  >
                    Đổi mật khẩu
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-m2-outline hover:text-m2-on-surface-variant transition-colors"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {!success ? (
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                      {/* Current Password */}
                      <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-m2-on-surface mb-1">
                          Mật khẩu hiện tại
                        </label>
                        <div className="relative">
                          <input
                            id="currentPassword"
                            name="currentPassword"
                            type={showPasswords.current ? 'text' : 'password'}
                            value={formData.currentPassword}
                            onChange={handleInputChange}
                            className="appearance-none block w-full pr-10 px-3 py-2 border border-m2-outline rounded-md placeholder-m2-outline focus:outline-none focus:ring-m2-primary focus:border-m2-primary sm:text-sm"
                            placeholder="Nhập mật khẩu hiện tại"
                            disabled={isLoading}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-2.5 text-m2-outline hover:text-m2-on-surface-variant"
                            onClick={() => togglePasswordVisibility('current')}
                          >
                            {showPasswords.current ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* New Password */}
                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-m2-on-surface mb-1">
                          Mật khẩu mới
                        </label>
                        <div className="relative">
                          <input
                            id="newPassword"
                            name="newPassword"
                            type={showPasswords.new ? 'text' : 'password'}
                            value={formData.newPassword}
                            onChange={handleInputChange}
                            className="appearance-none block w-full pr-10 px-3 py-2 border border-m2-outline rounded-md placeholder-m2-outline focus:outline-none focus:ring-m2-primary focus:border-m2-primary sm:text-sm"
                            placeholder="Nhập mật khẩu mới"
                            disabled={isLoading}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-2.5 text-m2-outline hover:text-m2-on-surface-variant"
                            onClick={() => togglePasswordVisibility('new')}
                          >
                            {showPasswords.new ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                          <div className={`mt-2 text-xs ${getPasswordStrengthColor()}`}>
                            {validationErrors.length === 0 ? (
                              <span className="flex items-center">
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Mật khẩu mạnh
                              </span>
                            ) : (
                              <ul className="list-disc list-inside space-y-1">
                                {validationErrors.map((err, idx) => (
                                  <li key={idx}>{err}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-m2-on-surface mb-1">
                          Xác nhận mật khẩu mới
                        </label>
                        <div className="relative">
                          <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className="appearance-none block w-full pr-10 px-3 py-2 border border-m2-outline rounded-md placeholder-m2-outline focus:outline-none focus:ring-m2-primary focus:border-m2-primary sm:text-sm"
                            placeholder="Nhập lại mật khẩu mới"
                            disabled={isLoading}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-2.5 text-m2-outline hover:text-m2-on-surface-variant"
                            onClick={() => togglePasswordVisibility('confirm')}
                          >
                            {showPasswords.confirm ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                          <p className="mt-1 text-xs text-red-600">Mật khẩu xác nhận không khớp</p>
                        )}
                      </div>

                      {error && (
                        <div className="rounded-md bg-m2-error/10 p-3">
                          <p className="text-sm text-m2-error">{error}</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 flex gap-3">
                      <button
                        type="button"
                        className="flex-1 inline-flex justify-center rounded-md border border-m2-outline bg-m2-surface-container px-4 py-2 text-sm font-medium text-m2-on-surface hover:bg-m2-surface-low focus:outline-none focus:ring-2 focus:ring-m2-primary focus:ring-offset-2"
                        onClick={handleClose}
                        disabled={isLoading}
                      >
                        Hủy
                      </button>
                      <button
                        type="submit"
                        className="flex-1 inline-flex justify-center rounded-eh-sm border border-transparent bg-eh-forest-ink px-4 py-2 text-sm font-medium text-eh-cream hover:bg-eh-forest-shadow focus:outline-none focus:ring-2 focus:ring-eh-forest-ink focus:ring-offset-2 disabled:bg-m2-outline disabled:cursor-not-allowed"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Đang xử lý...
                          </>
                        ) : (
                          'Đổi mật khẩu'
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="mt-2">
                    <div className="flex flex-col items-center text-center py-4">
                      <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
                      <h4 className="text-lg font-semibold text-m2-on-surface mb-2">
                        Đổi mật khẩu thành công!
                      </h4>
                      <p className="text-sm text-m2-on-surface-variant mb-4">
                        Mật khẩu của bạn đã được cập nhật. Vui lòng sử dụng mật khẩu mới để đăng nhập lần sau.
                      </p>
                      <p className="text-xs text-m2-on-surface-variant mb-4">
                        Đang chuyển hướng tới hệ thống học tập...
                      </p>
                      <button
                        type="button"
                        className="w-full inline-flex justify-center rounded-eh-sm border border-transparent bg-eh-forest-ink px-4 py-2 text-sm font-medium text-eh-cream hover:bg-eh-forest-shadow focus:outline-none focus:ring-2 focus:ring-eh-forest-ink focus:ring-offset-2"
                        onClick={handleClose}
                      >
                        Đóng
                      </button>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ChangePasswordModal;
